import { useMemo, useState } from 'react';
import type {
  BrandId,
  CreateModelPlanRequest,
  ModelPlanDetail,
  ModelPlanEntryInput,
  ModelPlanSkuInput,
  ModelPlanTargetNamespace,
  UpdateModelPlanRequest,
} from '@nadoutong/contracts';
import { BRAND_IDS, DEFAULT_BRAND_ID, normalizeBrandId } from '@nadoutong/contracts';
import { Eye, EyeOff, Trash2 } from 'lucide-react';
import { useModelPlanTargets } from '../../api/modelPlans.ts';
import { coerceErrorMessage } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Chip,
  Dialog,
  FormField,
  Input,
  Select,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui/index.ts';
import {
  fmtRate,
  groupDefaultCausesBreach,
  humanizeFloorBreaches,
  isModelPlanFloorError,
  liveBreachesFromResolved,
  modelPlanFloorBreachesFromError,
  parsePositiveRate,
  resolvePlanSkuRates,
} from './floorBreaches.ts';
import type { PlanFloorBreach, SkuResolvedRate } from './floorBreaches.ts';
import { FloorBreachesList, floorHintText } from './preview.tsx';

// Deliberately NOT the platform-wide DEFAULT_BRAND_ID: new model plans are
// primarily minted for the nxtpath brand (owner decision, 2026-09-05), so
// the create dialog starts there. The floor column, live validation and
// targets fetch all key off this select — switching it refetches per brand.
const CREATE_DEFAULT_BRAND: BrandId = 'nxtpath';

interface SkuDraft {
  readonly sku_id: string;
  visible: boolean;
  /** Empty string = inherit the parent entry default. */
  rate: string;
}

interface EntryDraft {
  readonly namespace_id: string;
  default_rate: string;
}

export interface PlanWritePayload {
  readonly create: CreateModelPlanRequest | null;
  readonly update: UpdateModelPlanRequest;
  readonly resolvedSkus: readonly SkuResolvedRate[];
}

function draftFromPlan(plan: ModelPlanDetail | null): {
  brand: BrandId;
  name: string;
  description: string;
  entries: EntryDraft[];
  skus: SkuDraft[];
} {
  if (plan === null) {
    return {
      brand: CREATE_DEFAULT_BRAND,
      name: '',
      description: '',
      entries: [],
      skus: [],
    };
  }
  const brand = normalizeBrandId(plan.brand) ?? CREATE_DEFAULT_BRAND;
  return {
    brand,
    name: plan.name,
    description: plan.description ?? '',
    entries: plan.entries.map((entry) => ({
      namespace_id: entry.namespace_id,
      default_rate: String(entry.default_rate_multiplier),
    })),
    skus: plan.skus.map((sku) => ({
      sku_id: sku.sku_id,
      visible: sku.visible,
      rate: sku.rate_multiplier === null ? '' : String(sku.rate_multiplier),
    })),
  };
}

/**
 * SKUs already on the plan (visible or hidden) are reviewed. Target SKUs
 * of the plan's groups that are absent from `plan.skus` are new arrivals.
 * Create mode has no persisted SKU set, so it returns null.
 */
function reviewedSkuIdsFromPlan(
  mode: 'create' | 'edit',
  plan: ModelPlanDetail | null,
): ReadonlySet<string> | null {
  if (mode !== 'edit' || plan === null) return null;
  return new Set(plan.skus.map((sku) => sku.sku_id));
}

function toInputs(
  entries: readonly EntryDraft[],
  skus: readonly SkuDraft[],
): { entries: ModelPlanEntryInput[]; skus: ModelPlanSkuInput[] } | { error: 'rate' } {
  const entryInputs: ModelPlanEntryInput[] = [];
  for (const entry of entries) {
    const rate = parsePositiveRate(entry.default_rate);
    if (rate === null) return { error: 'rate' };
    entryInputs.push({
      namespace_id: entry.namespace_id,
      default_rate_multiplier: rate,
    });
  }
  const skuInputs: ModelPlanSkuInput[] = [];
  for (const sku of skus) {
    const trimmed = sku.rate.trim();
    if (trimmed.length === 0) {
      skuInputs.push({ sku_id: sku.sku_id, visible: sku.visible, rate_multiplier: null });
      continue;
    }
    const rate = parsePositiveRate(sku.rate);
    if (rate === null) return { error: 'rate' };
    skuInputs.push({ sku_id: sku.sku_id, visible: sku.visible, rate_multiplier: rate });
  }
  return { entries: entryInputs, skus: skuInputs };
}

export function PlanEditorDialog({
  mode,
  plan,
  onClose,
  onSubmit,
  busy,
  error,
  onDismissError,
}: {
  readonly mode: 'create' | 'edit';
  readonly plan: ModelPlanDetail | null;
  readonly onClose: () => void;
  readonly onSubmit: (payload: PlanWritePayload) => void;
  readonly busy: boolean;
  readonly error: Error | null;
  readonly onDismissError: () => void;
}): JSX.Element {
  const t = useT();
  const initial = draftFromPlan(plan);
  const [brand, setBrand] = useState<BrandId>(initial.brand);
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description);
  const [entries, setEntries] = useState<EntryDraft[]>(initial.entries);
  const [skus, setSkus] = useState<SkuDraft[]>(initial.skus);
  const [addNamespace, setAddNamespace] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [blockingBreaches, setBlockingBreaches] = useState<readonly PlanFloorBreach[] | null>(
    null,
  );

  const targetsBrand = mode === 'create' ? brand : (normalizeBrandId(plan?.brand ?? brand) ?? brand);
  const targets = useModelPlanTargets(targetsBrand, true);
  const namespaces = targets.data?.namespaces ?? [];
  const namespaceById = useMemo(() => {
    const map = new Map<string, ModelPlanTargetNamespace>();
    for (const ns of namespaces) map.set(ns.id, ns);
    return map;
  }, [namespaces]);

  const entryIds = new Set(entries.map((entry) => entry.namespace_id));
  const addable = namespaces.filter((ns) => !entryIds.has(ns.id));

  const reviewedSkuIds = useMemo(
    () => reviewedSkuIdsFromPlan(mode, plan),
    [mode, plan],
  );

  const resolvedSkus = useMemo(
    () => resolvePlanSkuRates(entries, skus, namespaces),
    [entries, skus, namespaces],
  );
  const liveBreaches = useMemo(
    () => liveBreachesFromResolved(resolvedSkus),
    [resolvedSkus],
  );
  const liveBySkuId = useMemo(() => {
    const map = new Map<string, PlanFloorBreach>();
    for (const breach of liveBreaches) map.set(breach.sku_id, breach);
    return map;
  }, [liveBreaches]);
  const serverWire = modelPlanFloorBreachesFromError(error);
  const serverBreaches =
    serverWire === null ? null : humanizeFloorBreaches(serverWire, resolvedSkus);
  const panelBreaches = blockingBreaches ?? serverBreaches;

  function addGroup(namespaceId: string): void {
    const ns = namespaceById.get(namespaceId);
    if (ns === undefined) return;
    setEntries((current) => [
      ...current,
      {
        namespace_id: ns.id,
        default_rate: ns.sales_floor_multiplier === null ? '1' : String(ns.sales_floor_multiplier),
      },
    ]);
    setSkus((current) => [
      ...current,
      ...ns.skus
        .filter((sku) => !current.some((row) => row.sku_id === sku.id))
        .map((sku) => ({ sku_id: sku.id, visible: true, rate: '' })),
    ]);
    setAddNamespace('');
  }

  function removeGroup(namespaceId: string): void {
    const ns = namespaceById.get(namespaceId);
    const skuIds = new Set((ns?.skus ?? []).map((sku) => sku.id));
    setEntries((current) => current.filter((entry) => entry.namespace_id !== namespaceId));
    setSkus((current) => current.filter((sku) => !skuIds.has(sku.sku_id)));
  }

  function patchSku(skuId: string, patch: Partial<SkuDraft>): void {
    setSkus((current) =>
      current.map((row) => (row.sku_id === skuId ? { ...row, ...patch } : row)),
    );
  }

  function submit(): void {
    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setValidationError(t('model_plans.editor.error.name'));
      return;
    }
    if (entries.length === 0) {
      setValidationError(t('model_plans.editor.error.entries'));
      return;
    }
    const parsed = toInputs(entries, skus);
    if ('error' in parsed) {
      setValidationError(t('model_plans.editor.error.rate'));
      return;
    }
    const resolved = resolvePlanSkuRates(entries, skus, namespaces);
    const breaches = liveBreachesFromResolved(resolved);
    if (breaches.length > 0) {
      setBlockingBreaches(breaches);
      setValidationError(null);
      return;
    }
    setValidationError(null);
    if (mode === 'create') {
      onSubmit({
        create: {
          brand,
          name: trimmedName,
          description: description.trim().length === 0 ? undefined : description.trim(),
          entries: parsed.entries,
          skus: parsed.skus,
        },
        update: {},
        resolvedSkus: resolved,
      });
      return;
    }
    onSubmit({
      create: null,
      update: {
        name: trimmedName,
        description: description.trim().length === 0 ? null : description.trim(),
        entries: parsed.entries,
        skus: parsed.skus,
      },
      resolvedSkus: resolved,
    });
  }

  function dismissBlock(): void {
    setBlockingBreaches(null);
    onDismissError();
  }

  const blocked = panelBreaches !== null && panelBreaches.length > 0;
  const title = blocked
    ? t('model_plans.floor.block_title')
    : mode === 'create'
      ? t('model_plans.create.title')
      : t('model_plans.edit.title', { name: plan?.name ?? '' });

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
      title={title}
      closeLabel={t('common.close')}
      size="xl"
      testId={mode === 'create' ? 'model-plans-create-dialog' : 'model-plans-edit-dialog'}
      footer={
        blocked ? (
          <Button
            variant="primary"
            onClick={dismissBlock}
            data-testid="model-plans-floor-block-back"
          >
            {t('model_plans.floor.block_back')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              loading={busy}
              data-testid={
                mode === 'create' ? 'model-plans-create-submit' : 'model-plans-edit-preview'
              }
            >
              {mode === 'create'
                ? t('model_plans.editor.submit_create')
                : t('model_plans.editor.submit_edit')}
            </Button>
          </>
        )
      }
    >
      {blocked && panelBreaches !== null ? (
        <div className="flex flex-col gap-3" data-testid="model-plans-floor-block">
          <p className="m-0 text-dense text-paper-mid">{t('model_plans.floor.block_body')}</p>
          <FloorBreachesList
            breaches={panelBreaches}
            testId="model-plans-floor-block-list"
          />
        </div>
      ) : (
      <div className="flex flex-col gap-4">
        {mode === 'create' ? (
          <FormField label={t('model_plans.editor.brand')} fieldId="model-plans-editor-brand">
            <Select
              id="model-plans-editor-brand"
              value={brand}
              onValueChange={(next) => {
                const parsed = normalizeBrandId(next);
                if (parsed === null) return;
                setBrand(parsed);
                setEntries([]);
                setSkus([]);
              }}
              options={BRAND_IDS.map((id) => ({ value: id, label: id }))}
              mono
              testId="model-plans-editor-brand"
            />
          </FormField>
        ) : null}
        <FormField label={t('model_plans.editor.name')} fieldId="model-plans-editor-name">
          <Input
            id="model-plans-editor-name"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            placeholder={t('model_plans.editor.name_placeholder')}
            data-testid="model-plans-editor-name"
          />
        </FormField>
        <FormField
          label={t('model_plans.editor.description')}
          fieldId="model-plans-editor-description"
        >
          <Input
            id="model-plans-editor-description"
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
            placeholder={t('model_plans.editor.description_placeholder')}
            data-testid="model-plans-editor-description"
          />
        </FormField>

        <FormField
          label={t('model_plans.editor.add_namespace')}
          fieldId="model-plans-editor-add-namespace"
          hint={addable.length === 0 ? t('model_plans.editor.namespace_empty') : undefined}
        >
          <Select
            id="model-plans-editor-add-namespace"
            value={addNamespace.length === 0 ? undefined : addNamespace}
            onValueChange={(next) => {
              if (next.length > 0) addGroup(next);
            }}
            options={addable.map((ns) => ({ value: ns.id, label: ns.slug }))}
            placeholder={t('model_plans.editor.namespace_placeholder')}
            disabled={addable.length === 0}
            testId="model-plans-editor-add-namespace"
          />
        </FormField>

        {entries.map((entry) => {
          const ns = namespaceById.get(entry.namespace_id);
          const slug = ns?.slug ?? entry.namespace_id;
          const groupSkus = ns?.skus ?? [];
          const groupInvalid = groupDefaultCausesBreach(liveBreaches, entry.namespace_id);
          const newArrivalCount =
            reviewedSkuIds === null
              ? 0
              : groupSkus.filter((sku) => !reviewedSkuIds.has(sku.id)).length;
          return (
            <section
              key={entry.namespace_id}
              className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well px-3 py-3"
              data-testid={`model-plans-editor-group-${slug}`}
            >
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="font-mono text-body font-medium text-paper">{slug}</span>
                  <span className="text-micro text-paper-low">
                    {ns === undefined
                      ? t('model_plans.editor.sales_floor_unset')
                      : ns.sales_floor_multiplier === null
                        ? t('model_plans.editor.sales_floor_unset')
                        : t('model_plans.editor.sales_floor', {
                            floor: fmtRate(ns.sales_floor_multiplier),
                          })}
                    {ns?.has_unconfigured_sales_cost === true
                      ? ` · ${t('model_plans.editor.sales_floor_partial')}`
                      : ''}
                  </span>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <FormField
                    label={t('model_plans.editor.default_rate')}
                    fieldId={`model-plans-editor-rate-${slug}`}
                    error={
                      groupInvalid ? t('model_plans.floor.group_default_error') : undefined
                    }
                  >
                    <Input
                      id={`model-plans-editor-rate-${slug}`}
                      mono
                      type="number"
                      step="0.0001"
                      value={entry.default_rate}
                      invalid={groupInvalid}
                      onChange={(event) => {
                        const next = event.currentTarget.value;
                        setEntries((current) =>
                          current.map((row) =>
                            row.namespace_id === entry.namespace_id
                              ? { ...row, default_rate: next }
                              : row,
                          ),
                        );
                      }}
                      wrapperClassName="w-28"
                      data-testid={`model-plans-editor-rate-${slug}`}
                    />
                  </FormField>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeGroup(entry.namespace_id)}
                    data-testid={`model-plans-editor-remove-${slug}`}
                  >
                    <Trash2 aria-hidden="true" className="h-3.5 w-3.5" />
                    {t('model_plans.editor.remove_namespace')}
                  </Button>
                </div>
              </div>

              {newArrivalCount > 0 ? (
                <p
                  className="m-0 text-dense text-warn"
                  data-testid={`model-plans-editor-group-new-${slug}`}
                >
                  {t('model_plans.editor.new_arrival_group', { count: newArrivalCount })}
                </p>
              ) : null}

              {groupSkus.length === 0 ? (
                <p className="m-0 text-dense text-paper-low">{t('model_plans.editor.no_skus')}</p>
              ) : (
                <Table>
                  <THead>
                    <Tr interactive={false}>
                      <Th>{t('model_plans.editor.sku_visible')}</Th>
                      <Th>SKU</Th>
                      <Th>{t('model_plans.editor.sku_rate')}</Th>
                      <Th>{t('model_plans.editor.sku_floor_header')}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {groupSkus.map((targetSku) => {
                      const draft =
                        skus.find((row) => row.sku_id === targetSku.id) ?? {
                          sku_id: targetSku.id,
                          visible: false,
                          rate: '',
                        };
                      const rowBreach = liveBySkuId.get(targetSku.id);
                      const overrideCause = rowBreach?.source === 'sku_override';
                      const isNewArrival =
                        reviewedSkuIds !== null && !reviewedSkuIds.has(targetSku.id);
                      return (
                        <Tr
                          key={targetSku.id}
                          interactive={false}
                          className={rowBreach === undefined ? undefined : 'bg-bad-dim/40'}
                          data-testid={`model-plans-editor-sku-${targetSku.id}`}
                        >
                          <Td>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const existing = skus.some((row) => row.sku_id === targetSku.id);
                                if (!existing) {
                                  setSkus((current) => [
                                    ...current,
                                    {
                                      sku_id: targetSku.id,
                                      visible: true,
                                      rate: '',
                                    },
                                  ]);
                                  return;
                                }
                                patchSku(targetSku.id, { visible: !draft.visible });
                              }}
                              data-testid={`model-plans-editor-sku-visible-${targetSku.id}`}
                            >
                              {draft.visible ? (
                                <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                              ) : (
                                <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
                              )}
                              {draft.visible
                                ? t('model_plans.editor.sku_visible')
                                : t('model_plans.editor.sku_hidden')}
                            </Button>
                          </Td>
                          <Td>
                            <div className="flex flex-col">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-paper">{targetSku.label}</span>
                                {isNewArrival ? (
                                  <Chip
                                    tone="warn"
                                    lamp={false}
                                    testId={`model-plans-new-chip-${targetSku.id}`}
                                  >
                                    {t('model_plans.editor.new_arrival_sku')}
                                  </Chip>
                                ) : null}
                              </div>
                              <span className="font-mono text-micro text-paper-low">
                                {targetSku.model}
                              </span>
                            </div>
                          </Td>
                          <Td>
                            <div className="flex flex-col gap-1">
                              <Input
                                mono
                                type="number"
                                step="0.0001"
                                value={draft.rate}
                                invalid={overrideCause}
                                placeholder={t('model_plans.editor.sku_inherit')}
                                onChange={(event) => {
                                  const next = event.currentTarget.value;
                                  const existing = skus.some((row) => row.sku_id === targetSku.id);
                                  if (!existing) {
                                    setSkus((current) => [
                                      ...current,
                                      {
                                        sku_id: targetSku.id,
                                        visible: draft.visible,
                                        rate: next,
                                      },
                                    ]);
                                    return;
                                  }
                                  patchSku(targetSku.id, { rate: next });
                                }}
                                wrapperClassName="w-28"
                                data-testid={`model-plans-editor-sku-rate-${targetSku.id}`}
                              />
                              {rowBreach === undefined ? null : (
                                <span
                                  role="alert"
                                  className="text-label text-bad-bright"
                                  data-testid={`model-plans-editor-sku-floor-hint-${targetSku.id}`}
                                >
                                  {floorHintText(rowBreach, t)}
                                </span>
                              )}
                            </div>
                          </Td>
                          <Td mono>
                            {targetSku.sales_floor_multiplier === null
                              ? t('model_plans.editor.sales_floor_unset')
                              : fmtRate(targetSku.sales_floor_multiplier)}
                          </Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              )}
            </section>
          );
        })}

        {validationError !== null ? (
          <p
            role="alert"
            data-testid="model-plans-editor-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {validationError}
          </p>
        ) : null}
        {error !== null && serverBreaches === null ? (
          <p
            role="alert"
            data-testid="model-plans-editor-server-error"
            className="m-0 rounded-lg border border-bad-line bg-bad-dim/50 px-3 py-2 text-dense text-bad-bright"
          >
            {isModelPlanFloorError(error)
              ? t('model_plans.floor.unparsable')
              : coerceErrorMessage(error)}
          </p>
        ) : null}
      </div>
      )}
    </Dialog>
  );
}
